import {
  Card,
  Divider,
  Form,
  InputNumber,
  Space,
  Tooltip,
  Typography
} from 'antd'
import { useFFEStore } from '../../../../store/ffeStore'
import { LineItem } from '../../../../types/ffe'
import { formatCurrency } from '../../../../utils/currency'
import { HelpCircle } from 'lucide-react'
const { Text } = Typography

export const FinancialPanel = ({ item }: { item: LineItem }) => {
  const updateLineItem = useFFEStore((state) => state.updateLineItem)
  const materials = useFFEStore((state) => state.document.materials)
  const handleUpdate = (updates: Partial<typeof item>) => {
    updateLineItem(item.id, updates)
  }
  // Calculate margin percentage
  const margin =
    (((item.sellPrice || 0) - (item.costPrice || 0)) / (item.costPrice || 1)) *
    100

  const handleMarginChange = (marginPercent: number | null) => {
    // if (isUpdatingRef.current || marginPercent === null) return
    if (marginPercent === null) return

    // isUpdatingRef.current = true
    const costPrice = item.costPrice || 0
    const newSellPrice = costPrice * (1 + marginPercent / 100)
    handleUpdate({
      margin: marginPercent,
      sellPrice: Number(newSellPrice.toFixed(2))
    })
    // isUpdatingRef.current = false
  }

  const updateCostPrice = (value: number | null) => {
    if (value === null) return
    // if (value) {
    handleUpdate({
      costPrice: value || 0,
      sellPrice: value + (value * item.margin) / 100
    })
    // } else {
    //   onUpdate({ costPrice: value || 0 })
    // }
  }

  const updateSellPrice = (value: number | null) => {
    if (value === null) return
    handleUpdate({
      margin: ((value - item.costPrice) / item.costPrice) * 100,
      sellPrice: value || 0
    })
  }
  const changeQuantity = (value: number | null) => {
    const newValue = value || 0
    // update quantity for all materials

    handleUpdate({
      quantity: newValue
    })
  }

  // Calculate totals
  const itemCost = (item.costPrice || 0) * (item.quantity || 0)

  const finishesItemCost =
    item.materials?.reduce((total, mat) => {
      const material = materials.find((m) => m.id === mat.materialId)
      if (!material || material.materialType !== 'finish') return total
      return total + material.costPerUnit * mat.quantity
    }, 0) || 0

  const accessoriesItemCost =
    item.materials?.reduce((total, mat) => {
      const material = materials.find((m) => m.id === mat.materialId)
      if (!material || material.materialType !== 'accessory') return total
      return total + material.costPerUnit * mat.quantity
    }, 0) || 0

  const finishesCost = finishesItemCost * item.quantity
  const accessoriesCost = accessoriesItemCost * item.quantity

  const totalCost = itemCost + finishesCost + accessoriesCost

  const finishesItemSell =
    item.materials?.reduce((total, mat) => {
      const material = materials.find((m) => m.id === mat.materialId)
      if (!material || material.materialType !== 'finish') return total
      return total + material.sellPricePerUnit * mat.quantity
    }, 0) || 0

  const accessoriesItemSell =
    item.materials?.reduce((total, mat) => {
      const material = materials.find((m) => m.id === mat.materialId)
      if (!material || material.materialType !== 'accessory') return total
      return total + material.sellPricePerUnit * mat.quantity
    }, 0) || 0

  const finishesSell = finishesItemSell * item.quantity
  const accessoriesSell = accessoriesItemSell * item.quantity

  // Calculate total including materials
  const itemSell = (item.sellPrice || 0) * (item.quantity || 0)
  const totalSell = itemSell + finishesSell + accessoriesSell

  const blendedMargin = (totalSell / totalCost) * 100 - 100

  return (
    <div className="overflow-y-auto p-6 max-h-[calc(100vh-110px)]">
      <div className="space-y-6">
        <Form layout="vertical">
          <Space className="w-full" size="large">
            <Form.Item label="Quantity" className="flex-1">
              <InputNumber
                value={item.quantity}
                onChange={changeQuantity}
                min={0}
                className="w-full"
              />
            </Form.Item>

            <Form.Item label="Cost Price" className="flex-1">
              <InputNumber
                value={item.costPrice}
                onChange={updateCostPrice}
                prefix="$"
                min={0}
                precision={2}
                className="w-full"
              />
            </Form.Item>
          </Space>

          <Space className="w-full" size="large">
            <Form.Item label="Margin %" className="flex-1">
              <InputNumber
                value={Number(margin.toFixed(1))}
                onChange={handleMarginChange}
                min={-100}
                precision={1}
                className="w-full"
              />
            </Form.Item>

            <Form.Item label="Sell Price" className="flex-1">
              <InputNumber
                value={item.sellPrice}
                onChange={updateSellPrice}
                prefix="$"
                min={0}
                precision={2}
                className="w-full"
              />
            </Form.Item>
          </Space>
        </Form>

        <Card size="small" title="Financial Breakdown">
          <div className="space-y-2">
            <div className="flex justify-between">
              <Text>
                Item Cost ({item.quantity} × {formatCurrency(item.costPrice)}
                ):
              </Text>
              <Text>{formatCurrency(itemCost)}</Text>
            </div>

            <div className="flex justify-between">
              <Text>
                Finishes Cost ({item.quantity} ×{' '}
                {formatCurrency(finishesItemCost)}):
              </Text>
              <Text>{formatCurrency(finishesCost)}</Text>
            </div>
            <div className="flex justify-between">
              <Text>
                Accessories Cost ({item.quantity} ×{' '}
                {formatCurrency(accessoriesItemCost)}):
              </Text>
              <Text>{formatCurrency(accessoriesCost)}</Text>
            </div>

            <div className="flex justify-between font-medium">
              <Text>Total Cost:</Text>
              <Text>{formatCurrency(totalCost)}</Text>
            </div>
            <Divider className="my-2" />
            <div className="flex justify-between">
              <Text>
                Item Sell ({item.quantity} × {formatCurrency(item.sellPrice)}
                ):
              </Text>
              <Text>{formatCurrency(itemSell)}</Text>
            </div>

            <div className="flex justify-between">
              <Text>
                Finishes Sell ({item.quantity} ×{' '}
                {formatCurrency(finishesItemSell)}):
              </Text>
              <Text>{formatCurrency(finishesSell)}</Text>
            </div>
            <div className="flex justify-between">
              <Text>
                Accessories Sell ({item.quantity} ×{' '}
                {formatCurrency(accessoriesItemSell)}):
              </Text>
              <Text>{formatCurrency(accessoriesSell)}</Text>
            </div>
            <div className="flex justify-between font-medium">
              <Text>Total Sell:</Text>
              <Text>{formatCurrency(totalSell)}</Text>
            </div>
            <Divider className="my-2" />

            <div className="flex justify-between text-green-600">
              <Text>Profit:</Text>
              <Text>{formatCurrency(totalSell - totalCost)}</Text>
            </div>
            <div className="flex justify-between text-green-600">
              <Tooltip title="This may differ from your specified line item margin if there are finishes or accessories with differing margins.">
                <Text className="flex items-center gap-2">
                  Blended Margin:{' '}
                  <HelpCircle className="text-gray-500 w-4 h-4" />
                </Text>
              </Tooltip>
              <Text>{blendedMargin.toFixed(2)}%</Text>
            </div>
          </div>
        </Card>

        {item.materials && item.materials.length > 0 && (
          <Card size="small" title="Material Cost Breakdown">
            <div className="space-y-2">
              {item.materials.map((mat) => {
                const material = materials.find((m) => m.id === mat.materialId)
                if (!material) return null
                const cost = material.costPerUnit * mat.quantity

                return (
                  <div key={mat.materialId} className="flex justify-between">
                    <Text>
                      {material.name} ({mat.quantity}
                      {material.unit && ` ${material.unit}`}):
                    </Text>
                    <Text>{formatCurrency(cost)}</Text>
                  </div>
                )
              })}
            </div>
          </Card>
        )}
      </div>
    </div>
  )
}
